import React, { useEffect, useRef, useState } from 'react';
import { COLOR } from '@src/styles/color';
import { formatAudioTime } from '@src/utils/time';

import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Tooltip from '@src/components/Tooltip';
import apis from '@src/apis';
import { useTranslation } from 'react-i18next';
import CommonButton from '@src/new-components/Button';
import CommonSwitch from '@src/new-components/Switch';
import NoiseAwareRoundedIcon from '@src/assets/icons/noise-aware.svg';
import { truncateFilename } from '@src/services/instantVoiceCloning';
import Typography from '@src/new-components/Typography';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import actions from '@src/redux/actions';
import { useDispatch } from 'react-redux';
import { STEPS } from '@src/constants/instantVoiceCloning';
import WrapperStepContent from '../WrapperStepContent';
import {
  StyledAudioInfo,
  StyledAudioPlayer,
  StyledNoiseReductionToggle,
  StyledPreviewAudio,
} from './index.style';

const PreviewAudio = ({
  metadataAudio,
  onSetStep,
  sentences,
  onResetMetadataAudio,
  setAudioDraft,
  prevStep,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isDenoise, setIsDenoise] = useState(true);

  const audioRef = useRef(null);

  useEffect(() => {
    if (!isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
  }, [isPlaying]);

  const handlePlayPause = () => {
    if (!metadataAudio?.audioLink) return;
    setIsPlaying((prev) => !prev);
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleLoadedMetadata = (e) => {
    if (metadataAudio?.recordTime) {
      setDuration(metadataAudio?.recordTime);
    } else {
      setDuration(e.target.duration);
    }
  };

  const handleCreateInstantVoiceTraining = async () => {
    const audioDraft = {
      audioLink: metadataAudio?.audioLink,
      denoise: isDenoise,
      audioId: metadataAudio?.audioId,
      ...(sentences?.id && { sentenceId: sentences?.id }),
    };
    try {
      const response =
        await apis.instantVoiceTraining.createInstantVoiceTraining(audioDraft);
      if (response?.status) {
        setAudioDraft({
          ...audioDraft,
          voiceId: response?.result?.voiceId,
        });
        onSetStep(STEPS.CREATE_VOICE_CLONE);
      }
    } catch (error) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }
  };

  return (
    <WrapperStepContent
      onSetStep={onSetStep}
      onResetMetadataAudio={onResetMetadataAudio}
      prevStep={prevStep}
    >
      <StyledPreviewAudio>
        {/* Audio player UI */}
        <StyledAudioPlayer>
          <CommonButton
            onClick={handlePlayPause}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '50%',
              minWidth: '40px !important',
              minHeight: '40px !important',
              padding: '0 !important',
            }}
            disabled={!metadataAudio?.audioLink}
          >
            {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </CommonButton>
          <StyledAudioInfo>
            <Typography
              component="span"
              variant="body-16-medium"
              color={COLOR.black.default}
            >
              {truncateFilename({
                filename: metadataAudio?.fileName,
                maxLength: 25,
                maxLengthExt: 5,
                maxLengthName: 20,
              })}
            </Typography>
            <Typography
              component="span"
              variant="body-14-regular"
              color={COLOR.descriptionUpload}
            >
              {formatAudioTime(duration)}
            </Typography>
          </StyledAudioInfo>
          <audio
            ref={audioRef}
            src={metadataAudio?.audioLink}
            onEnded={handleAudioEnded}
            onLoadedMetadata={handleLoadedMetadata}
            style={{ display: 'none' }}
          >
            <track kind="captions" />
          </audio>
        </StyledAudioPlayer>

        <StyledNoiseReductionToggle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={NoiseAwareRoundedIcon} alt="noise-aware" />
            <Typography
              component="span"
              variant="body-14-regular"
              color={COLOR.black.default}
            >
              {t('noiseReduction')}
            </Typography>
            <Tooltip arrow placement="top" title={t('noiseReductionTooltip')}>
              <InfoRoundedIcon className="info-rounded-icon" />
            </Tooltip>
          </div>
          <CommonSwitch
            variant="ios"
            checked={isDenoise}
            onChange={() => setIsDenoise(!isDenoise)}
          />
        </StyledNoiseReductionToggle>

        <CommonButton
          onClick={handleCreateInstantVoiceTraining}
          textProps={{
            variantText: 'body-14-bold',
            color: COLOR.darkBlue,
          }}
          variant="contained"
          color="primary"
          customStyle={{
            marginTop: '64px',
            height: '48px',
            padding: '12px 40px',
          }}
        >
          {t('voiceCloningLabelV2')}
        </CommonButton>
      </StyledPreviewAudio>
    </WrapperStepContent>
  );
};

export default PreviewAudio;
